import React, { useEffect, useState, useRef } from "react";
import api, { apiErr, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Upload, Loader2, Save, Scissors } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState({ business_name: "", phone: "", address: "", logo_url: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef();

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload?kind=logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", fileUrl(data.storage_path));
      toast.success("Logo terunggah");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings", form); setForm(data); toast.success("Pengaturan usaha disimpan"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400">Memuat pengaturan...</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Pengaturan Usaha</h1>
        <p className="text-sm text-slate-500">Identitas usaha ini akan tampil di SPK dan Nota yang dicetak.</p>
      </div>

      <Card className="p-6 border-slate-200/80 shadow-sm space-y-6">
        <div className="flex items-center gap-2 text-blue-600"><Building2 className="h-5 w-5" /><span className="font-heading font-bold">Profil Usaha</span></div>

        <div className="flex flex-col sm:flex-row items-start gap-5">
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-slate-200 bg-slate-50">
              {form.logo_url ? <img src={form.logo_url} alt="logo" className="h-full w-full object-contain" /> : <Scissors className="h-8 w-8 text-slate-300" />}
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} />
            <Button data-testid="btn-upload-logo" type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" /> Logo</>}
            </Button>
          </div>

          <div className="flex-1 w-full space-y-4">
            <div className="space-y-1.5">
              <Label>Nama Usaha</Label>
              <Input data-testid="settings-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Nama workshop bordir Anda" />
            </div>
            <div className="space-y-1.5">
              <Label>No. HP / Telepon</Label>
              <Input data-testid="settings-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0812-xxxx-xxxx" />
            </div>
            <div className="space-y-1.5">
              <Label>Alamat Usaha</Label>
              <Textarea data-testid="settings-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Alamat lengkap workshop" />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button data-testid="btn-save-settings" className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Simpan Pengaturan</>}
          </Button>
        </div>
      </Card>
    </div>
  );
}
